'use client';

import { useCallback, useEffect, useRef, useState, type Ref } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Combobox, Stack, type ComboboxOption } from '@leanin/ui';
import { getApiClient } from '../../lib/api-client';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

/** Structured place payload accepted by every server write path that used to
 *  take a free-text location field. Mirrors `PlacePayloadSchema` in
 *  `@leanin/shared/views/place`. */
export type PlaceSelection = {
  placeId: string;
  placeText: string;
  placeLat: number;
  placeLng: number;
  placeCountryCode: string | null;
};

/** A stored location that is not a geocoded place: legacy free text from
 *  before structured places. A `city`/`region` picker shows it as its value
 *  until the member picks a place or clears it. */
export type StoredLocationText = { readonly storedText: string };

/** What a member's location field holds: a geocoded place, a stored location
 *  that is not one, or nothing. */
export type LocationValue = PlaceSelection | StoredLocationText | null;

function samePlace(a: PlaceSelection | null, b: LocationValue): boolean {
  if (a === null || b === null) return a === b;
  if ('storedText' in b) return false;
  return (
    a.placeId === b.placeId &&
    a.placeText === b.placeText &&
    a.placeLat === b.placeLat &&
    a.placeLng === b.placeLng &&
    a.placeCountryCode === b.placeCountryCode
  );
}

/** The `place` a profile save sends for a location field: nothing while it
 *  holds what was last saved (the stored text included, which the save
 *  keeps), else the picked place, or null when the member cleared it. */
export function locationChange(
  current: LocationValue,
  saved: LocationValue,
): { readonly place?: PlaceSelection | null } {
  if (current !== null && 'storedText' in current) return {};
  return samePlace(current, saved) ? {} : { place: current };
}

/** A `venue`-mode pick: the place plus the venue's own name when it is an
 *  establishment ("The Battery"), null for a bare address. */
export type VenueSelection = PlaceSelection & { placeName: string | null };

/** Mode controls how Google Places autocomplete is biased.
 *  - `city`   — restrict to localities/towns.
 *  - `region` — localities plus standalone states/provinces and countries
 *               (for a member's own location, where "Ireland" alone is valid).
 *  - `venue`  — allow POIs, addresses, and cities; emits a `VenueSelection`. */
export type PlacePickerMode = 'city' | 'region' | 'venue';

type CommonProps = {
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  invalid?: boolean;
  /** ISO-3166 alpha-2 code; suggestions come only from this country. */
  country?: string;
  /** Reads as a search field (Combobox `search`): a magnifier, no chevron. */
  search?: boolean;
  /** The input's id; `Field.Control` sets it so the Field label names the input. */
  id?: string;
  /** Set by `Field.Control` so the Field's description and error reach the input. */
  'aria-describedby'?: string;
  /** Set by `Field.Control` while the Field shows an error. */
  'aria-invalid'?: boolean;
  /** Set by `Field.Control` while the Field's `required` prop is true. */
  'aria-required'?: boolean;
  /** The search input, e.g. to focus it from a deep link. */
  ref?: Ref<HTMLInputElement>;
};

export type PlacePickerProps = CommonProps &
  (
    | {
        mode: 'city' | 'region';
        value: LocationValue;
        onChange: (next: PlaceSelection | null) => void;
      }
    | {
        mode: 'venue';
        value: VenueSelection | null;
        onChange: (next: VenueSelection | null) => void;
      }
  );

function newSessionToken(): string {
  return crypto.randomUUID();
}

/** What the input shows for a committed pick — "The Battery, 717 Battery St,
 *  San Francisco…" for a named venue, the formatted address otherwise. */
function selectionLabel(s: PlaceSelection | VenueSelection): string {
  return 'placeName' in s && s.placeName !== null
    ? `${s.placeName}, ${s.placeText}`
    : s.placeText;
}

// The combobox option for stored text, which has no place id to key it.
const STORED_TEXT_OPTION = 'stored-location-text';

/** The combobox option that shows the picker's current value. */
function valueOption(value: PlaceSelection | StoredLocationText | VenueSelection): ComboboxOption {
  return 'storedText' in value
    ? { value: STORED_TEXT_OPTION, label: value.storedText }
    : { value: value.placeId, label: selectionLabel(value) };
}

/**
 * Single-select place autocomplete backed by `/api/v1/geo/places/*`. Replaces
 * the legacy OSM-Nominatim `LocationPicker`. The Combobox primitive owns the
 * UI shell; this component handles the autocomplete-debounce, session token
 * lifecycle, and the details fetch on commit.
 *
 * Session token contract (Google Places billing): one token is reused across
 * every keystroke of a single "search session"; when the user commits an
 * option we fire the details fetch with that same token so Google bills the
 * keystroke run + final lookup as one autocomplete session. A fresh token is
 * minted for the next search.
 */
export function PlacePicker({
  mode,
  value,
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
  invalid = false,
  country,
  search = false,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ref,
}: PlacePickerProps) {
  const t = useTranslations('Common');
  // Shown both inside the dropdown (in place of "No matches") and as an inline
  // message when a Places request fails — so a provider error never presents
  // as a silently blank field. Local dev without a real GOOGLE_PLACES_API_KEY
  // is the common trigger; a Google outage is the same surface in prod.
  const unavailableMessage = t('widgets.placePicker.unavailable');
  // The combobox stores its committed value as the `placeId` (option.value);
  // the visible label comes from the option's `label` (`selectionLabel`).
  const [options, setOptions] = useState<ComboboxOption[]>(() =>
    value === null ? [] : [valueOption(value)],
  );
  const [loading, setLoading] = useState(false);
  // True when the last Places request (autocomplete or details) failed —
  // drives the dropdown error copy and the inline message instead of letting
  // the field sit silently empty.
  const [providerError, setProviderError] = useState(false);

  // Active session token. Reset to a fresh UUID after each successful commit
  // (see `commitSelection`) and on initial mount.
  const sessionTokenRef = useRef<string>(newSessionToken());

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cache of `placeId → VenueSelection` for results pulled this session.
  // Keeps the details fetch idempotent when the user clicks the same row
  // twice (and lets us short-circuit re-hydration of an already-resolved
  // selection that's still in the option list).
  const resolvedRef = useRef<Map<string, VenueSelection>>(new Map());

  // Venue mode hands the parent the venue name; city/region modes get the
  // plain place, so their write paths never carry a field they don't store.
  const emit = useCallback(
    (next: VenueSelection | null) => {
      // `onChange` is either mode's callback; both accept a VenueSelection
      // (a superset of PlaceSelection), so only the copy below is mode-aware.
      if (mode === 'venue' || next === null) {
        onChange(next);
        return;
      }
      onChange({
        placeId: next.placeId,
        placeText: next.placeText,
        placeLat: next.placeLat,
        placeLng: next.placeLng,
        placeCountryCode: next.placeCountryCode,
      });
    },
    [mode, onChange],
  );

  useEffect(() => {
    // Keep the option list in sync with the parent-controlled value so a
    // programmatic clear (`onChange(null)`) and a fresh non-null hydration
    // both reflect in the combobox display.
    if (value === null) {
      setOptions([]);
      return;
    }
    const current = valueOption(value);
    setOptions((prev) => {
      if (prev.some((o) => o.value === current.value)) return prev;
      return [current];
    });
  }, [value]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const handleQueryChange = useCallback(
    (query: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();

      const trimmed = query.trim();
      if (trimmed.length < MIN_QUERY_LEN) {
        setOptions([]);
        setProviderError(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      timerRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        try {
          const client = getApiClient();
          const { data, error } = await client.POST(
            '/api/v1/geo/places/autocomplete',
            {
              body: {
                q: trimmed,
                types: mode,
                sessionToken: sessionTokenRef.current,
                ...(country !== undefined ? { country } : {}),
              },
              signal: controller.signal,
            },
          );
          if (controller.signal.aborted) return;
          if (error !== undefined || data === undefined) {
            setOptions([]);
            setProviderError(true);
            return;
          }
          const opts: ComboboxOption[] = data.results.map((r) => ({
            value: r.placeId,
            label: r.primaryText,
            sublabel: r.secondaryText,
          }));
          setProviderError(false);
          setOptions(opts);
        } catch (err) {
          if ((err as Error).name === 'AbortError') return;
          setOptions([]);
          setProviderError(true);
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [mode, country],
  );

  const commitSelection = useCallback(
    async (placeId: string) => {
      const cached = resolvedRef.current.get(placeId);
      if (cached !== undefined) {
        setProviderError(false);
        emit(cached);
        sessionTokenRef.current = newSessionToken();
        return;
      }
      const client = getApiClient();
      const { data, error } = await client.GET(
        '/api/v1/geo/places/{placeId}',
        {
          params: {
            path: { placeId },
            query: { sessionToken: sessionTokenRef.current },
          },
        },
      );
      if (error !== undefined || data === undefined) {
        // Throwing here would break the combobox's commit flow (the value
        // would stay committed-but-unresolved), so leave the selection
        // untouched — but surface the failure instead of swallowing it, so
        // the field doesn't just sit blank with no explanation.
        setProviderError(true);
        sessionTokenRef.current = newSessionToken();
        return;
      }
      setProviderError(false);
      const resolved: VenueSelection = {
        placeId: data.placeId,
        placeText: data.text,
        placeName: data.name,
        placeLat: data.lat,
        placeLng: data.lng,
        placeCountryCode: data.countryCode,
      };
      resolvedRef.current.set(placeId, resolved);
      // Also refresh the visible label so a commit-from-suggestion shows the
      // formatted text the server resolved (e.g. "Boston, Massachusetts,
      // USA" rather than the suggestion's primaryText "Boston").
      setOptions((prev) =>
        prev.map((o) =>
          o.value === placeId ? { ...o, label: selectionLabel(resolved) } : o,
        ),
      );
      emit(resolved);
      sessionTokenRef.current = newSessionToken();
    },
    [emit],
  );

  const handleChange = useCallback(
    (next: string | null) => {
      // The stored text is already the value; there is no place to resolve.
      if (next === STORED_TEXT_OPTION) return;
      if (next === null) {
        emit(null);
        // Clearing also ends the current session — a re-pick is a fresh one.
        sessionTokenRef.current = newSessionToken();
        return;
      }
      // Combobox emits the option's `value` (== placeId). Resolve via the
      // details endpoint, then push the structured selection up.
      void commitSelection(next);
    },
    [commitSelection, emit],
  );

  return (
    <Stack gap="xs">
      <Combobox
        ref={ref}
        options={options}
        value={value === null ? null : valueOption(value).value}
        onChange={handleChange}
        onQueryChange={handleQueryChange}
        noInternalFilter
        search={search}
        loading={loading}
        placeholder={placeholder ?? t(PLACEHOLDER_KEY_BY_MODE[mode])}
        emptyLabel={t('widgets.placePicker.noMatches')}
        noOptionsLabel={t('widgets.placePicker.typeToSearch')}
        error={providerError}
        errorLabel={unavailableMessage}
        disabled={disabled}
        invalid={invalid || providerError}
        ariaLabel={ariaLabel ?? t('widgets.placePicker.ariaLabel')}
        {...(id !== undefined ? { id } : {})}
        {...(ariaDescribedBy !== undefined ? { 'aria-describedby': ariaDescribedBy } : {})}
        {...(ariaInvalid !== undefined ? { 'aria-invalid': ariaInvalid } : {})}
        {...(ariaRequired !== undefined ? { 'aria-required': ariaRequired } : {})}
      />
      {providerError ? (
        // Mirrors FieldError's caption styling (var(--type-caption) +
        // var(--destructive)) so the failure is visible whether the dropdown
        // is open or closed.
        <span
          role="alert"
          aria-live="polite"
          style={{ font: 'var(--type-caption)', color: 'var(--destructive)' }}
        >
          {unavailableMessage}
        </span>
      ) : null}
    </Stack>
  );
}

const PLACEHOLDER_KEY_BY_MODE = {
  city: 'widgets.placePicker.placeholderCity',
  region: 'widgets.placePicker.placeholderRegion',
  venue: 'widgets.placePicker.placeholderVenue',
} as const;
